import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TOAST_EVENT, type ToastPayload, type ToastType } from '../lib/toast';
import { cn } from '../lib/cn';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  durationMs: number;
}

/**
 * Mounts once near the root of the app. Listens for `showToast()` dispatches
 * on `window` and renders a stack of auto-dismissing toasts in the top-right.
 *
 * Lifted from My.Tms.Old/components/ToastContainer.tsx, modernised with:
 *   • aria-live region so screen readers announce changes
 *   • per-type icon + close button
 *   • timeout cleanup on unmount (tab close / route change)
 *   • `durationMs: 0` makes a toast sticky (no auto-dismiss)
 */
export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    let counter = 0;
    const timeouts = new Map<number, ReturnType<typeof setTimeout>>();

    const dismiss = (id: number) => {
      const handle = timeouts.get(id);
      if (handle) {
        clearTimeout(handle);
        timeouts.delete(id);
      }
      setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ToastPayload>).detail;
      if (!detail?.message) return;
      const id = ++counter;
      const next: ToastItem = {
        id,
        message: detail.message,
        type: detail.type ?? 'info',
        durationMs: detail.durationMs ?? 3000,
      };
      setToasts((prev) => [...prev, next]);
      if (next.durationMs > 0) {
        timeouts.set(
          id,
          setTimeout(() => dismiss(id), next.durationMs),
        );
      }
    };

    window.addEventListener(TOAST_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(TOAST_EVENT, handler as EventListener);
      timeouts.forEach((h) => clearTimeout(h));
      timeouts.clear();
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed right-4 top-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastView
          key={t.id}
          toast={t}
          onDismiss={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
        />
      ))}
    </div>
  );
}

const TYPE_STYLES: Record<ToastType, { container: string; icon: typeof Info }> = {
  info: { container: 'bg-slate-800 text-white', icon: Info },
  success: { container: 'bg-green-600 text-white', icon: CheckCircle2 },
  warning: { container: 'bg-amber-500 text-white', icon: AlertTriangle },
  error: { container: 'bg-red-600 text-white', icon: XCircle },
};

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  const { container, icon: Icon } = TYPE_STYLES[toast.type];
  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex items-start gap-2 rounded-md px-3 py-2 text-sm shadow-md',
        container,
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div className="flex-1 break-words">{toast.message}</div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="-mr-1 -mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm text-white/70 hover:bg-white/10 hover:text-white"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
